import React from "react";
import { clubApplicantShroffVoucherUrl } from "../../services/coachClubService";

const PRIMARY = "#1d4ed8";
const NAVY = "#0f1c3f";
const LIGHT = "#e8f0fe";

const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("active") || s.includes("paid") || s.includes("approved")) return { bg: "#dcfce7", color: "#15803d" };
  if (s.includes("pending") || s.includes("await")) return { bg: "#fef3c7", color: "#b45309" };
  if (s.includes("reject") || s.includes("expired") || s.includes("fail")) return { bg: "#fee2e2", color: "#b91c1c" };
  return { bg: "#e5e7eb", color: "#4b5563" };
};

const Pill = ({ children }) => {
  const c = statusColor(children);
  return (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: c.bg, color: c.color }}>
      {children || "—"}
    </span>
  );
};

// "My Clubs" — lists every club this coach belongs to (GET /api/coach/clubs),
// lets them switch which one is active (POST /api/coach/active-club), shows
// any additional-club registration still awaiting payment, and offers
// "Register Another Club" (RegisterAdditionalClubModal). This is the first
// UI in the app for CoachClubController — see coachClubService.js.
//
// Rendered as an anchored dropdown panel (not a full-screen modal) — the
// caller wraps the trigger button + this component in a `relative` container,
// same pattern as the header's notification/profile dropdowns: a fixed
// click-away layer plus an `absolute` panel below the button.
//
// Props: `clubs` (array), `pendingClubs` (array), `activeClubId`,
// `switchingId` (club id currently mid-switch, or null), `loading` (initial
// fetch in flight), `onSwitch(clubId)`, `onRegisterAnother()`, `onClose()`.
export default function ClubSwitcherModal({
  clubs = [],
  pendingClubs = [],
  activeClubId,
  switchingId = null,
  loading = false,
  onSwitch,
  onRegisterAnother,
  onClose,
}) {
  return (
    <>
      {/* Click-away layer */}
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-100 z-40 p-5"
        role="dialog"
        aria-modal="true"
      >
        <h2 className="text-base font-bold" style={{ color: NAVY }}>My Clubs</h2>
        <p className="text-xs text-gray-500 mb-4">Switch which club you're managing, or register a new one.</p>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <svg className="animate-spin w-7 h-7" fill="none" viewBox="0 0 24 24" stroke={PRIMARY}>
              <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" />
              <path className="opacity-75" strokeWidth="4" strokeLinecap="round" d="M4 12a8 8 0 018-8" />
            </svg>
          </div>
        ) : (
          <>
            {clubs.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">You're not attached to any club yet.</p>
            ) : (
              <div className="space-y-2">
                {clubs.map((c) => {
                  const isActive = c.id === activeClubId;
                  return (
                    <div
                      key={c.id}
                      className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition ${
                        isActive ? "border-blue-600 bg-blue-50/60" : "border-gray-200"
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: LIGHT }}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={PRIMARY}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-14h.01M11 7h.01M15 7h.01M7 11h.01M11 11h.01M15 11h.01M7 15h.01M11 15h.01M15 15h.01" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold truncate" style={{ color: NAVY }}>{c.club_name}</p>
                          {c.is_primary && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">Primary</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-400">{c.club_code}</span>
                          <Pill>{c.club_status}</Pill>
                        </div>
                      </div>
                      {isActive ? (
                        <span className="text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0" style={{ color: PRIMARY, backgroundColor: LIGHT }}>
                          Active
                        </span>
                      ) : (
                        <button
                          onClick={() => onSwitch?.(c.id)}
                          disabled={switchingId != null}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg border-2 flex-shrink-0 transition disabled:opacity-60"
                          style={{ borderColor: PRIMARY, color: PRIMARY }}
                        >
                          {switchingId === c.id ? "Switching…" : "Switch"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {pendingClubs.length > 0 && (
              <div className="mt-5 pt-5 border-t border-gray-100">
                <p className="text-xs font-bold text-gray-500 mb-2">Pending Registrations</p>
                <div className="space-y-2">
                  {pendingClubs.map((p) => {
                    const voucherUrl = clubApplicantShroffVoucherUrl(p.process_id, p.external_payment_reference || p.payment_reference);
                    return (
                      <div key={p.process_id} className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate text-gray-800">{p.club_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400">{p.reg_no}</span>
                            <Pill>{p.payment_status}</Pill>
                          </div>
                        </div>
                        {voucherUrl && (
                          <a
                            href={voucherUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg border-2 flex-shrink-0 transition"
                            style={{ borderColor: PRIMARY, color: PRIMARY }}
                          >
                            Voucher
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        <button
          onClick={onRegisterAnother}
          className="w-full mt-5 flex items-center justify-center gap-2 border-2 font-semibold py-2.5 rounded-xl text-sm transition"
          style={{ borderColor: PRIMARY, color: PRIMARY }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Register Another Club
        </button>
      </div>
    </>
  );
}
